'use client';

import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faClock,
  faRoad,
} from '@fortawesome/pro-solid-svg-icons';
import { useDispatch } from 'react-redux';
import { setHoveredLegIndex } from 'src/redux';
import type { RouteDirections } from 'src/hooks/routes/types';

/** Shown in place of distance and duration for a leg Mapbox couldn't route */
const APPROXIMATE_LEG_TEXT = 'Straight line, not routed';

interface DirectionsListProps {
  directions: RouteDirections;
}

/**
 * DirectionsList - Displays turn-by-turn directions for a route
 *
 * Shows legs between stops with expandable step-by-step instructions.
 */
const DirectionsList: React.FC<DirectionsListProps> = ({ directions }) => {
  const dispatch = useDispatch();
  const [expandedLegs, setExpandedLegs] = useState<Set<number>>(new Set());

  const handleLegMouseEnter = useCallback(
    (legIndex: number) => {
      dispatch(setHoveredLegIndex(legIndex));
    },
    [dispatch]
  );

  const handleLegMouseLeave = useCallback(() => {
    dispatch(setHoveredLegIndex(null));
  }, [dispatch]);

  const toggleLeg = (legIndex: number) => {
    setExpandedLegs((prev) => {
      const next = new Set(prev);
      if (next.has(legIndex)) {
        next.delete(legIndex);
      } else {
        next.add(legIndex);
      }
      return next;
    });
  };

  const hasApproximateLegs = directions.legs.some((leg) => leg.isApproximate);

  return (
    <div className="space-y-2" data-testid="directions-list">
      {/* Summary */}
      <div className="flex items-center gap-4 p-3 bg-neutral-50 rounded-lg">
        <div className="flex items-center gap-2 text-sm text-neutral-600">
          <Icon icon={faRoad} className="w-4 h-4 text-neutral-400" />
          <span className="font-medium">{directions.totalDistanceText}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-neutral-600">
          <Icon icon={faClock} className="w-4 h-4 text-neutral-400" />
          <span className="font-medium">{directions.totalDurationText}</span>
        </div>
      </div>
      {hasApproximateLegs && (
        <p className="text-xs text-neutral-500" data-testid="directions-partial-note">
          Some sections couldn&apos;t be routed and are drawn as straight lines.
          The totals above only include routed sections.
        </p>
      )}

      {/* Legs */}
      {directions.legs.map((leg, legIndex) => (
        <div
          key={legIndex}
          className="border border-neutral-200 rounded-lg overflow-hidden"
          onMouseEnter={() => handleLegMouseEnter(legIndex)}
          onMouseLeave={handleLegMouseLeave}
          data-testid={`directions-leg-${legIndex}`}
        >
          {/* Leg header */}
          <button
            type="button"
            onClick={() => toggleLeg(legIndex)}
            className="w-full flex items-center gap-3 p-3 bg-white hover:bg-neutral-50 transition-colors text-left"
            aria-expanded={expandedLegs.has(legIndex)}
            aria-label={`Leg ${legIndex + 1}: ${leg.startAddress.split(',')[0]} to ${leg.endAddress.split(',')[0]}, ${leg.isApproximate ? APPROXIMATE_LEG_TEXT : `${leg.distanceText}, ${leg.durationText}`}`}
            data-testid={`directions-leg-toggle-${legIndex}`}
          >
            <Icon
              icon={expandedLegs.has(legIndex) ? faChevronDown : faChevronRight}
              className="w-3 h-3 text-neutral-400"
            />
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold shrink-0">
              {legIndex + 1}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-neutral-900 truncate">
                {leg.startAddress.split(',')[0]} → {leg.endAddress.split(',')[0]}
              </p>
              <p className="text-xs text-neutral-500">
                {leg.isApproximate
                  ? APPROXIMATE_LEG_TEXT
                  : `${leg.distanceText} · ${leg.durationText}`}
              </p>
            </div>
          </button>

          {/* Expanded steps */}
          {expandedLegs.has(legIndex) && (
            <div className="border-t border-neutral-200 bg-neutral-50">
              {leg.steps.map((step, stepIndex) => (
                <div
                  key={stepIndex}
                  className="flex items-start gap-3 p-3 border-b border-neutral-100 last:border-b-0"
                  data-testid={`directions-step-${legIndex}-${stepIndex}`}
                >
                  <div className="w-6 h-6 flex items-center justify-center shrink-0">
                    <div className="w-2 h-2 rounded-full bg-neutral-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-sm text-neutral-700"
                      dangerouslySetInnerHTML={{ __html: step.instructions }}
                    />
                    <p className="text-xs text-neutral-500 mt-0.5">
                      {step.distanceText} · {step.durationText}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default DirectionsList;
